import React, {
  useState,
  useRef,
  useMemo,
  useEffect,
  useCallback,
} from "react";
import {
  MonitorSmartphoneIcon,
  PointerIcon,
  ChevronRight,
  PlusIcon,
  ArrowLeftIcon,
  MousePointerIcon,
  HomeIcon,
  SmartphoneIcon,
  TabletIcon,
  LaptopIcon,
  MonitorIcon,
  Moon,
} from "lucide-react";
import { cn } from "@/lib/utils";
import ReactDOM from "react-dom";
import {
  FLEX_DIRECTION_OPTIONS,
  FLEX_WRAP_OPTIONS,
  ALIGN_CONTENT_OPTIONS,
  JUSTIFY_CONTENT_OPTIONS,
} from "./variantOptions";
import {
  TEXT_ALIGN_OPTIONS,
  TEXT_DECORATION_LINE_OPTIONS,
} from "./variantOptions";
import { ResetIcon } from "@radix-ui/react-icons";

export type VariantType =
  | "default"
  | "sm"
  | "md"
  | "lg"
  | "xl"
  | "2xl"
  | "hover"
  | "active"
  | "focus"
  | "dark";

type VariantControlProps = {
  valueByVariant: Record<
    string,
    { value: any; displayValue: string | null; active: boolean }
  >;
  onVariantChange: (variant: VariantType, value: any) => void;
  onClearVariant: (variant: VariantType) => void;
  children: (
    variant: VariantType | null,
    expanded: boolean,
    setExpanded: (v: boolean) => void
  ) => React.ReactNode;
  className?: string;
  tooltipContent?: string;
  currentFlexDirection: string;
  currentJustifyContent: string;
  currentAlignItems: string;
  controlType?:
    | "align"
    | "align-content"
    | "justify-content"
    | "direction-wrap"
    | "text-align"
    | "text-decoration-line"
    | "text-color";
  anchorRef?: React.RefObject<HTMLElement>;
  modalOpen?: boolean;
  setModalOpen?: (open: boolean) => void;
};

const breakpointVariants = [
  {
    variant: "default",
    label: "default",
    desc: ">= 0rem (0px)",
    icon: <HomeIcon className="w-4 h-4 text-icons-secondary" />,
  },
  {
    variant: "sm",
    label: "sm",
    desc: ">= 40rem (640px)",
    icon: <SmartphoneIcon className="w-4 h-4 text-icons-secondary" />,
  },
  {
    variant: "md",
    label: "md",
    desc: ">= 48rem (768px)",
    icon: <TabletIcon className="w-4 h-4 text-icons-secondary" />,
  },
  {
    variant: "lg",
    label: "lg",
    desc: ">= 64rem (1024px)",
    icon: <LaptopIcon className="w-4 h-4 text-icons-secondary" />,
  },
  {
    variant: "xl",
    label: "xl",
    desc: ">= 80rem (1280px)",
    icon: <MonitorIcon className="w-4 h-4 text-icons-secondary" />,
  },
  {
    variant: "2xl",
    label: "2xl",
    desc: ">= 96rem (1536px)",
    icon: <MonitorIcon className="w-4 h-4 text-icons-secondary" />,
  },
];

const interactionVariants = [
  {
    variant: "hover",
    label: "hover",
    desc: "apply on hover",
    icon: <MousePointerIcon className="w-4 h-4 text-icons-secondary" />,
  },
  {
    variant: "active",
    label: "active",
    desc: "apply on active",
    icon: <PointerIcon className="w-4 h-4 text-icons-secondary" />,
  },
  {
    variant: "focus",
    label: "focus",
    desc: "apply on focus",
    icon: <PointerIcon className="w-4 h-4 text-icons-secondary" />,
  },
  {
    variant: "dark",
    label: "dark",
    desc: "apply in dark mode",
    icon: <Moon className="w-4 h-4 text-icons-secondary" />,
  },
];

export const VariantDot = ({
  valueByVariant,
}: {
  valueByVariant: VariantControlProps["valueByVariant"];
}) => {
  const hasVariants = Object.keys(valueByVariant).length >= 1;
  const activeCount = Object.keys(valueByVariant).filter(
    (variant) => valueByVariant[variant].active
  ).length;
  const onlyDefault =
    Object.keys(valueByVariant).length === 1 && valueByVariant["default"];
  return (
    <div
      className={cn(
        "absolute right-[-3px] top-[-3px] bg-background-CTA size-[6px] rounded-full z-[1000]",
        hasVariants && activeCount === 0 && "bg-white",
        !hasVariants && "hidden",
        onlyDefault && "hidden"
      )}
    />
  );
};

const justifyContentMap = ["flex-start", "center", "flex-end"];
const alignItemsMap = ["flex-start", "center", "flex-end"];
const modifyForReverse = (direction: string, alignment: string) => {
  if (direction?.endsWith("reverse")) {
    const reverseMap = {
      "flex-start": "flex-end",
      "flex-end": "flex-start",
      center: "center",
    };
    return reverseMap[alignment];
  }
  return alignment;
};
function getIndexFromStyles(
  direction: string,
  justifyContent: string,
  alignItems: string
) {
  if (alignItems === "start") alignItems = "flex-start";
  else if (alignItems === "end") alignItems = "flex-end";
  if (justifyContent === "start") justifyContent = "flex-start";
  else if (justifyContent === "end") justifyContent = "flex-end";
  justifyContent = modifyForReverse(direction, justifyContent);
  const justifyContentIndex = justifyContentMap.indexOf(justifyContent);
  const alignItemsIndex = alignItemsMap.indexOf(alignItems);
  if (justifyContentIndex === -1 || alignItemsIndex === -1) return -1;
  let index;
  if (!direction || direction.startsWith("row")) {
    index = alignItemsIndex * 3 + justifyContentIndex;
  } else {
    index = justifyContentIndex * 3 + alignItemsIndex;
  }
  return index;
}

const LayoutGraphicIcon = ({ index, row }: { index: number; row: boolean }) => {
  const rowStyles = "grow rounded-[1px] bg-icons-primary";
  let alignStyle = "";
  let justifyStyle = "";
  if (row) {
    if (index < 3) {
      alignStyle = "items-start";
    } else if (index < 6) {
      alignStyle = "items-center";
    } else {
      alignStyle = "items-end";
    }
  } else {
    if (index % 3 === 0) {
      alignStyle = "items-start";
    } else if (index % 3 === 1) {
      alignStyle = "items-center";
    } else {
      alignStyle = "items-end";
    }
  }
  return (
    <div
      className={cn(
        "flex h-5 w-5 gap-0.5 relative",
        row ? "flex-row" : "flex-col",
        alignStyle,
        justifyStyle
      )}
    >
      <div className={cn(rowStyles, !row ? "w-7/12" : "h-4/6")}></div>
      <div className={cn(rowStyles, !row ? "w-full" : "h-full")}></div>
      <div className={cn(rowStyles, !row ? "w-5/12" : "h-2/6")}></div>
    </div>
  );
};

const usePortalRoot = () => {
  const [portalRoot, setPortalRoot] = useState<HTMLElement | null>(null);
  useEffect(() => {
    const root = document.getElementById("styling-panel-portal-root");
    setPortalRoot(root);
  }, []);
  return portalRoot;
};

type VariantListProps = {
  variants: typeof breakpointVariants;
  valueByVariant: any;
  onVariantSelect: (variant: VariantType) => void;
  currentFlexDirection: string;
  currentJustifyContent: string;
  currentAlignItems: string;
  controlType?:
    | "align"
    | "align-content"
    | "justify-content"
    | "direction-wrap"
    | "text-align"
    | "text-decoration-line"
    | "text-color";
};

const VariantList = ({
  variants,
  valueByVariant,
  onVariantSelect,
  currentFlexDirection,
  currentJustifyContent,
  currentAlignItems,
  controlType,
}: VariantListProps) => {
  return (
    <div className="flex flex-col divide-y divide-secondary-n-800">
      {variants.map((bp) => {
        const variantEntry = valueByVariant[bp.variant];
        let directionIcon: React.ReactNode = null;
        let wrapIcon: React.ReactNode = null;
        if (
          variantEntry &&
          (variantEntry.flexDirection || variantEntry.flexWrap)
        ) {
          if (variantEntry.flexDirection) {
            const found = (
              typeof FLEX_DIRECTION_OPTIONS !== "undefined"
                ? FLEX_DIRECTION_OPTIONS
                : []
            ).find((opt) => opt.option.tailwind === variantEntry.flexDirection);
            directionIcon = found?.labelContent || null;
          }
          if (variantEntry.flexWrap) {
            const found = (
              typeof FLEX_WRAP_OPTIONS !== "undefined" ? FLEX_WRAP_OPTIONS : []
            ).find((opt) => opt.option.tailwind === variantEntry.flexWrap);
            wrapIcon = found?.labelContent || null;
          }
        }
        // Align-content and justify-content summary logic
        let alignContentIcon: React.ReactNode = null;
        let justifyContentIcon: React.ReactNode = null;
        if (variantEntry && variantEntry.value) {
          if (controlType === "align-content") {
            const foundAlign = ALIGN_CONTENT_OPTIONS.find(
              (opt) => opt.key === variantEntry.value
            );
            if (foundAlign) alignContentIcon = foundAlign.labelContent;
          } else if (controlType === "justify-content") {
            const foundJustify = JUSTIFY_CONTENT_OPTIONS.find(
              (opt) => opt.key === variantEntry.value
            );
            if (foundJustify) justifyContentIcon = foundJustify.labelContent;
          }
        }
        // Fallback to align logic for Align section only
        let alignIcon: React.ReactNode = null;
        if (controlType === "align") {
          const alignItems = variantEntry?.value || currentAlignItems;
          const flexDirection = currentFlexDirection;
          const justifyContent = currentJustifyContent;
          const index = getIndexFromStyles(
            flexDirection,
            justifyContent,
            alignItems
          );
          if (index !== -1) {
            alignIcon = (
              <LayoutGraphicIcon
                index={index}
                row={flexDirection.startsWith("row")}
              />
            );
          }
        }
        // For text-align and text-decoration-line, show the selected icon if set
        let customIcon: React.ReactNode = null;
        if (
          controlType === "text-align" &&
          variantEntry &&
          variantEntry.value
        ) {
          const found = TEXT_ALIGN_OPTIONS.find(
            (opt) => opt.key === variantEntry.value
          );
          if (found) customIcon = found.labelContent;
        }
        if (
          controlType === "text-decoration-line" &&
          variantEntry &&
          variantEntry.value
        ) {
          const found = TEXT_DECORATION_LINE_OPTIONS.find(
            (opt) => opt.key === variantEntry.value
          );
          if (found) customIcon = found.labelContent;
        }
        // For text-color, show a color swatch for the variant value just before the chevron, and do not render other icons
        let colorSwatch: React.ReactNode = null;
        let resolvedColor: string | null = null;
        let valueLabel: string = "";
        if (
          controlType === "text-color" &&
          variantEntry &&
          variantEntry.value
        ) {
          let color = variantEntry.value;
          if (typeof window !== "undefined" && color.startsWith("text-")) {
            const map = (window as any).tailwindClassesMap;
            if (map && map[color] && map[color].css) {
              const css = map[color].css;
              const match = css.match(/color:\s*([^;]+);?/);
              if (match) {
                resolvedColor = match[1].trim();
              }
            } else {
              const arbitraryMatch = color.match(/^text-\[(.+)\]$/);
              if (arbitraryMatch) {
                resolvedColor = arbitraryMatch[1];
              }
            }
          }
          if (!resolvedColor) {
            resolvedColor = color;
          }
          colorSwatch = (
            <span
              style={{
                display: "inline-block",
                width: 16,
                height: 16,
                borderRadius: 4,
                background: resolvedColor || "",
                border: "1px solid #333",
                marginRight: 4,
              }}
            />
          );
        } else if (
          [
            "direction-wrap",
            "gradient-direction",
            "from-position",
            "to-position",
            "via-position",
          ].includes(controlType ?? "") &&
          variantEntry &&
          variantEntry.value
        ) {
          valueLabel = variantEntry.displayValue || variantEntry.value;
        }
        valueLabel = valueLabel || "";
        const hasValue =
          !!variantEntry &&
          (variantEntry.flexDirection ||
            variantEntry.flexWrap ||
            variantEntry.value);
        return (
          <div
            key={bp.variant}
            className="px-3 py-2 flex flex-row justify-between items-center cursor-pointer hover:bg-accent"
            onClick={() => onVariantSelect(bp.variant as VariantType)}
          >
            <div className="flex flex-row space-x-2 items-center relative">
              {bp.icon}
              <span className="text-xs font-medium text-white">{bp.label}</span>
              <span className="text-xs text-secondary-n-500">{bp.desc}</span>
            </div>
            {controlType === "text-color" ? (
              variantEntry && variantEntry.value ? (
                <span className="flex items-center gap-1">
                  {colorSwatch}
                  <span className="text-xs text-white mr-2">
                    {valueLabel || ""}
                  </span>
                  <ChevronRight className="w-4 h-4 text-icons-secondary" />
                </span>
              ) : (
                <PlusIcon className="w-4 h-4 text-icons-secondary" />
              )
            ) : hasValue ? (
              <span className="flex items-center gap-1">
                {directionIcon}
                {wrapIcon}
                {alignContentIcon}
                {justifyContentIcon}
                {alignIcon}
                {customIcon}
                <ChevronRight className="w-4 h-4 text-icons-secondary" />
              </span>
            ) : (
              <PlusIcon className="w-4 h-4 text-icons-secondary" />
            )}
          </div>
        );
      })}
    </div>
  );
};

const VariantContent = React.memo(
  ({
    variant,
    expanded,
    setExpanded,
    children,
  }: {
    variant: VariantType | null;
    expanded: boolean;
    setExpanded: (v: boolean) => void;
    children: (
      variant: VariantType | null,
      expanded: boolean,
      setExpanded: (v: boolean) => void
    ) => React.ReactNode;
  }) => {
    if (!expanded || !variant) return null;
    return <>{children(variant, expanded, setExpanded)}</>;
  }
);

export const VariantControl: React.FC<VariantControlProps> = ({
  valueByVariant,
  onVariantChange,
  onClearVariant,
  children,
  className,
  tooltipContent,
  currentFlexDirection,
  currentJustifyContent,
  currentAlignItems,
  controlType,
  anchorRef: externalAnchorRef,

  // For variant controls that have an explicit button
  // All others are from the context menu
  modalOpen: modalOpenProp,
  setModalOpen: setModalOpenProp,
}) => {
  const asModal = Boolean(setModalOpenProp);

  const [modalMode, setModalMode] = useState<
    | { mode: "menu" }
    | { mode: "breakpoints" }
    | { mode: "interactions" }
    | {
        mode: "variant";
        variant: VariantType;
        from: "breakpoints" | "interactions";
      }
  >({ mode: "menu" });

  const internalAnchorRef = useRef<HTMLElement>(null);
  const anchorRef = externalAnchorRef || internalAnchorRef;
  const portalRoot = usePortalRoot();
  const [modalStyle, setModalStyle] = useState<React.CSSProperties>({});

  useEffect(() => {
    if (asModal && modalOpenProp && anchorRef.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      const modalWidth = 320;
      const modalHeight = 300;
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      let top;
      let left;
      if (spaceBelow < modalHeight && spaceAbove > modalHeight) {
        top = rect.top - modalHeight - 4;
        left = rect.left;
      } else {
        top = rect.bottom + 4;
        left = rect.left;
      }
      if (left + modalWidth > window.innerWidth) {
        left = window.innerWidth - modalWidth - 12;
      }
      if (left < 8) left = 8;
      setModalStyle({
        position: "absolute",
        top: Math.max(top, 8),
        left: left,
        width: modalWidth,
        zIndex: 100002,
        fontSize: "0.75rem",
      });
    }
  }, [modalOpenProp, anchorRef]);

  const handleBack = useCallback(() => {
    if (modalMode.mode === "menu") {
      setModalOpenProp?.(false);
    } else if (modalMode.mode === "variant") {
      setModalMode({ mode: modalMode.from });
    } else {
      setModalMode({ mode: "menu" });
    }
  }, [modalMode]);

  const handleMenuSelect = useCallback(
    (section: "breakpoints" | "interactions") => {
      setModalMode({ mode: section });
    },
    []
  );

  const handleVariantSelect = useCallback(
    (variant: VariantType) => {
      if (
        modalMode.mode === "breakpoints" ||
        modalMode.mode === "interactions"
      ) {
        setModalMode({ mode: "variant", variant, from: modalMode.mode });
      } else {
        setModalMode({ mode: "variant", variant, from: "breakpoints" }); // fallback
      }
    },
    [modalMode]
  );

  const breakpointCount = Object.keys(valueByVariant).filter(
    (v) =>
      ["default", "sm", "md", "lg", "xl", "2xl"].includes(v) &&
      valueByVariant[v]?.active
  ).length;
  const interactionCount = Object.keys(valueByVariant).filter(
    (v) =>
      ["hover", "active", "focus", "dark"].includes(v) &&
      valueByVariant[v]?.active
  ).length;

  const modalContent = useMemo(() => {
    if (modalMode.mode === "menu") {
      return (
        <div
          className="flex flex-col bg-popover shadow-md border rounded-md text-xs"
          style={{ width: 320 }}
        >
          <div className="flex flex-col divide-y divide-secondary-n-800">
            <div
              className="px-3 py-2 flex flex-row justify-between items-center cursor-pointer hover:bg-accent"
              onClick={() => handleMenuSelect("breakpoints")}
            >
              <span className="flex items-center gap-2">
                <MonitorSmartphoneIcon className="w-4 h-4 text-icons-secondary" />{" "}
                Responsive Breakpoints
              </span>
              {breakpointCount > 0 && (
                <span className="rounded-xs py-[2px] px-[4px] text-primary bg-icons-accent w-[15px] h-[16px] flex items-center justify-center">
                  {breakpointCount}
                </span>
              )}
              <ChevronRight className="w-4 h-4 text-icons-secondary ml-2" />
            </div>
            <div
              className="px-3 py-2 flex flex-row justify-between items-center cursor-pointer hover:bg-accent"
              onClick={() => handleMenuSelect("interactions")}
            >
              <span className="flex items-center gap-2">
                <PointerIcon className="w-4 h-4 text-icons-secondary" />{" "}
                Interaction States
              </span>
              {interactionCount > 0 && (
                <span className="rounded-xs py-[2px] px-[4px] text-primary bg-icons-accent w-[15px] h-[16px] flex items-center justify-center">
                  {interactionCount}
                </span>
              )}
              <ChevronRight className="w-4 h-4 text-icons-secondary ml-2" />
            </div>
            {Object.values(valueByVariant).some((v) => v.active) && (
              <div
                className="px-3 py-2 flex flex-row justify-between items-center cursor-pointer hover:bg-accent"
                onClick={() => {
                  Object.keys(valueByVariant).forEach((v) =>
                    onClearVariant(v as VariantType)
                  );
                  setModalOpenProp?.(false);
                }}
              >
                <span className="flex items-center gap-2">
                  <ResetIcon className="w-4 h-4" /> Clear style
                </span>
              </div>
            )}
          </div>
        </div>
      );
    } else if (modalMode.mode === "breakpoints") {
      return (
        <div
          className="flex flex-col bg-popover shadow-md border rounded-md text-xs"
          style={{ width: 320 }}
        >
          <div className="flex flex-row items-center px-2 py-2 border-b border-secondary-n-800 sticky top-0 z-10">
            <button
              className="mr-2 p-1 rounded hover:bg-secondary-n-900 focus:outline-none focus:ring-2 focus:ring-primary-500"
              onClick={handleBack}
              aria-label="Back to menu"
            >
              <ArrowLeftIcon className="w-4 h-4 text-icons-secondary" />
            </button>
            <span className="text-xs font-medium text-white">
              Responsive Breakpoints
            </span>
          </div>
          <VariantList
            variants={breakpointVariants}
            valueByVariant={valueByVariant}
            onVariantSelect={handleVariantSelect}
            currentFlexDirection={currentFlexDirection}
            currentJustifyContent={currentJustifyContent}
            currentAlignItems={currentAlignItems}
            controlType={controlType}
          />
        </div>
      );
    } else if (modalMode.mode === "interactions") {
      return (
        <div
          className="flex flex-col bg-popover shadow-md border rounded-md text-xs"
          style={{ width: 320 }}
        >
          <div className="flex flex-row items-center px-3 py-2 border-b border-secondary-n-800  sticky top-0 z-10">
            <button
              className="mr-2 p-1 rounded hover:bg-secondary-n-900 focus:outline-none focus:ring-2 focus:ring-primary-500"
              onClick={handleBack}
              aria-label="Back to menu"
            >
              <ArrowLeftIcon className="w-4 h-4 text-icons-secondary" />
            </button>
            <span className="text-xs font-medium text-white">
              Interaction States
            </span>
          </div>
          <VariantList
            variants={interactionVariants}
            valueByVariant={valueByVariant}
            onVariantSelect={handleVariantSelect}
            currentFlexDirection={currentFlexDirection}
            currentJustifyContent={currentJustifyContent}
            currentAlignItems={currentAlignItems}
            controlType={controlType}
          />
        </div>
      );
    } else if (modalMode.mode === "variant") {
      // Find the variant object in the correct list
      const variantObj =
        breakpointVariants.find((v) => v.variant === modalMode.variant) ||
        interactionVariants.find((v) => v.variant === modalMode.variant);
      return (
        <div
          className="flex flex-col bg-popover shadow-md border rounded-md text-xs"
          style={{ width: 320 }}
        >
          <div className="flex flex-row items-center px-3 py-2 border-b border-secondary-n-800 sticky top-0 z-10">
            <button
              className="mr-2 p-1 rounded hover:bg-secondary-n-900 focus:outline-none focus:ring-2 focus:ring-primary-500"
              onClick={handleBack}
              aria-label="Back to list"
            >
              <ArrowLeftIcon className="w-4 h-4 text-icons-secondary" />
            </button>
            <span className="flex items-center gap-2">
              {variantObj?.icon}
              <span className="text-xs font-medium text-white">
                {variantObj?.label || modalMode.variant}
              </span>
              <span className="text-xs text-secondary-n-500">
                {variantObj?.desc}
              </span>
            </span>
          </div>
          <div className="px-3 pb-2 pt-2">
            <VariantContent
              variant={modalMode.variant}
              expanded={true}
              setExpanded={() => setModalMode({ mode: "menu" })}
            >
              {(variant, expanded, setExpanded) => {
                return children(variant, expanded, (v) => {
                  setExpanded(v);
                  if (!v) setModalOpenProp?.(false);
                });
              }}
            </VariantContent>
          </div>
        </div>
      );
    }
    return null;
  }, [
    modalMode,
    valueByVariant,
    currentFlexDirection,
    currentJustifyContent,
    currentAlignItems,
    children,
    handleBack,
    handleMenuSelect,
    handleVariantSelect,
    controlType,
  ]);

  if (asModal) {
    return (
      <div
        className={cn("relative flex flex-row items-center", className)}
        ref={anchorRef as React.RefObject<HTMLDivElement>}
      >
        {modalOpenProp &&
          portalRoot &&
          ReactDOM.createPortal(
            <div
              data-inside-modal="true"
              onClick={(e) => {
                e.stopPropagation();
                setModalOpenProp?.(false);
              }}
              style={{ position: "fixed", inset: 0, zIndex: 100001 }}
            >
              <div
                data-inside-modal="true"
                onClick={(e) => e.stopPropagation()}
                style={modalStyle}
              >
                {modalContent}
              </div>
            </div>,
            portalRoot
          )}
      </div>
    );
  }

  return (
    <div className={cn("relative flex flex-row items-center", className)}>
      <div onClick={(e) => e.stopPropagation()} style={modalStyle}>
        {modalContent}
      </div>
    </div>
  );
};
